import {
  createForm as createFormApi,
  getForm as getFormApi,
  listFormFields as listFormFieldsApi,
  listForms as listFormsApi,
  patchForm as patchFormApi,
  replaceFormFields as replaceFormFieldsApi,
} from '@shared/api/forms';
import type {
  Form,
  FormCreateRequest,
  FormField,
  FormFieldInput,
  FormPatchRequest,
} from '@shared/types/form';

interface ListState {
  data: Form[] | null;
  loading: boolean;
  error: string | null;
}

const listState = $state<ListState>({ data: null, loading: false, error: null });
let listToken = 0;

function sortForms(items: Form[]): Form[] {
  return [...items].sort((a, b) => b.updated_at.localeCompare(a.updated_at));
}

export const formListStore = {
  get data(): Form[] | null {
    return listState.data;
  },
  get loading(): boolean {
    return listState.loading;
  },
  get error(): string | null {
    return listState.error;
  },
  async refresh(): Promise<void> {
    const token = ++listToken;
    listState.loading = true;
    listState.error = null;
    try {
      const res = await listFormsApi();
      if (token !== listToken) return;
      listState.data = sortForms(res.items);
    } catch (err) {
      if (token !== listToken) return;
      listState.error = err instanceof Error ? err.message : String(err);
    } finally {
      if (token === listToken) listState.loading = false;
    }
  },
  upsert(form: Form): void {
    const current = listState.data ?? [];
    listState.data = sortForms([form, ...current.filter((item) => item.id !== form.id)]);
  },
};

interface DetailState {
  data: Form | null;
  loading: boolean;
  error: string | null;
}

const detailState = $state<DetailState>({ data: null, loading: false, error: null });
let detailToken = 0;

export const formDetailStore = {
  get data(): Form | null {
    return detailState.data;
  },
  get loading(): boolean {
    return detailState.loading;
  },
  get error(): string | null {
    return detailState.error;
  },
  async load(id: string): Promise<void> {
    const token = ++detailToken;
    detailState.loading = true;
    detailState.error = null;
    try {
      const form = await getFormApi(id);
      if (token !== detailToken) return;
      detailState.data = form;
    } catch (err) {
      if (token !== detailToken) return;
      detailState.error = err instanceof Error ? err.message : String(err);
      detailState.data = null;
    } finally {
      if (token === detailToken) detailState.loading = false;
    }
  },
  setLocal(form: Form): void {
    detailState.data = form;
  },
  clear(): void {
    detailToken++;
    detailState.data = null;
    detailState.error = null;
    detailState.loading = false;
  },
};

interface FieldsState {
  data: FormField[] | null;
  loading: boolean;
  error: string | null;
}

const fieldsState = $state<FieldsState>({ data: null, loading: false, error: null });
let fieldsToken = 0;

function sortFields(items: FormField[]): FormField[] {
  return [...items].sort((a, b) => a.sort_order - b.sort_order);
}

export const formFieldsStore = {
  get data(): FormField[] | null {
    return fieldsState.data;
  },
  get loading(): boolean {
    return fieldsState.loading;
  },
  get error(): string | null {
    return fieldsState.error;
  },
  async load(id: string): Promise<void> {
    const token = ++fieldsToken;
    fieldsState.loading = true;
    fieldsState.error = null;
    try {
      const res = await listFormFieldsApi(id);
      if (token !== fieldsToken) return;
      fieldsState.data = sortFields(res.items);
    } catch (err) {
      if (token !== fieldsToken) return;
      fieldsState.error = err instanceof Error ? err.message : String(err);
      fieldsState.data = null;
    } finally {
      if (token === fieldsToken) fieldsState.loading = false;
    }
  },
  setLocal(fields: FormField[]): void {
    fieldsState.data = sortFields(fields);
  },
  clear(): void {
    fieldsToken++;
    fieldsState.data = null;
    fieldsState.error = null;
    fieldsState.loading = false;
  },
};

export async function createForm(body: FormCreateRequest): Promise<Form> {
  const form = await createFormApi(body);
  formListStore.upsert(form);
  return form;
}

export async function patchForm(id: string, body: FormPatchRequest): Promise<Form> {
  const form = await patchFormApi(id, body);
  formDetailStore.setLocal(form);
  formListStore.upsert(form);
  return form;
}

export async function replaceFormFields(
  id: string,
  body: FormFieldInput[],
): Promise<FormField[]> {
  const res = await replaceFormFieldsApi(id, body);
  formFieldsStore.setLocal(res.items);
  return sortFields(res.items);
}
